import { TRANSLATED_REPLACE_TERM_REASON } from '@src/utility/replaceTermLimit';
import { useMemo } from 'react';

import type {
  TranscriptContextMenuActions,
  TranscriptContextMenuDisabledReasons,
} from '@src/types.ts/general';

/** Why Remove Text is inert on a translated language (PLAT-11599). */
export const TRANSLATED_REMOVE_TEXT_REASON =
  'Text can only be removed in the original language';
/** Why Cut Text is inert on a translated language (PLAT-11599). */
export const TRANSLATED_CUT_TEXT_REASON =
  'Text can only be cut in the original language';
/**
 * PLAT-11602 — why every row but Fix Translation is inert when the translated
 * selection cannot be mapped back to a range of the original transcript.
 */
export const UNMAPPED_SELECTION_REASON =
  'Select text that lines up with the original transcript';

/**
 * PLAT-11602 — the reasons map for a selection that does not map to the
 * original. Every action is disabled; a row that already carries a
 * state-specific reason (Remove Text on a translation) keeps it, because a
 * better selection would not unlock that row — the language would.
 */
export function disableForUnmappedSelection(
  actions: TranscriptContextMenuActions[],
  reasons: TranscriptContextMenuDisabledReasons | undefined,
): TranscriptContextMenuDisabledReasons {
  const out: TranscriptContextMenuDisabledReasons = {};
  for (const action of actions) {
    out[action] = reasons?.[action] ?? UNMAPPED_SELECTION_REASON;
  }
  return out;
}

export interface TranslatedContextMenuGates {
  /** Surface gates — the host never offers the action, so the row is absent. */
  hideReplace: boolean;
  hideRemoveText: boolean;
  hideCutText: boolean;
  hideSaveAsClip: boolean;
  hideAddPhoto: boolean;
  hideAddOverlayGraphic: boolean;
  /** Overlay insert is only wired on the clip preview. */
  hasOverlayHandler: boolean;
  /** A punch-list photo is being added — swaps Add Photo for its busy row. */
  addedPunchListItemId: string | null | undefined;
}

export interface TranslatedContextMenuConfig {
  actions: TranscriptContextMenuActions[];
  disabledReasons: TranscriptContextMenuDisabledReasons;
}

/**
 * The action list the translated prose view offers after Fix Translation
 * (PLAT-10646), in the original-language menu's order.
 *
 * PLAT-11599 / PLAT-11601 — Replace Term, Remove Text and Cut Text are LISTED
 * but DISABLED with a reason rather than left out: removing or cutting text on a translation is not
 * supported yet (the translation would go stale against the original), and a
 * row that vanishes on a language switch reads as broken. Hiding stays reserved
 * for surfaces that never offer the action (reviewer mode passes
 * `hideRemoveText` / `hideCutText`), mirroring the original menu's filters in
 * `Transcript.showOptionsForWordWithIndexAtPosition`. Rule set:
 * `docs/design-system.md` → "Conditional actions: hide, disable, or swap".
 */
export function buildTranslatedContextMenuActions(
  gates: TranslatedContextMenuGates,
): TranslatedContextMenuConfig {
  const actions: TranscriptContextMenuActions[] = [];
  const disabledReasons: TranscriptContextMenuDisabledReasons = {};
  if (!gates.hideReplace) {
    actions.push('replace');
    disabledReasons.replace = TRANSLATED_REPLACE_TERM_REASON;
  }
  if (!gates.hideRemoveText) {
    actions.push('removeText');
    disabledReasons.removeText = TRANSLATED_REMOVE_TEXT_REASON;
  }
  if (!gates.hideCutText && !gates.hideRemoveText) {
    actions.push('cutText');
    disabledReasons.cutText = TRANSLATED_CUT_TEXT_REASON;
  }
  if (!gates.hideSaveAsClip) actions.push('saveAsClip');
  actions.push('saveAsPullQuote', 'reassignSpeaker');
  if (!gates.hideAddPhoto) {
    actions.push(gates.addedPunchListItemId ? 'addPhotoLoading' : 'addPhoto');
  }
  if (!gates.hideAddOverlayGraphic && gates.hasOverlayHandler) {
    actions.push('addOverlayGraphic');
  }
  return { actions, disabledReasons };
}

/** Memoized `buildTranslatedContextMenuActions` for `Transcript.tsx`. */
export function useTranslatedContextMenuActions(
  gates: TranslatedContextMenuGates,
): TranslatedContextMenuConfig {
  const {
    hideReplace,
    hideRemoveText,
    hideCutText,
    hideSaveAsClip,
    hideAddPhoto,
    hideAddOverlayGraphic,
    hasOverlayHandler,
    addedPunchListItemId,
  } = gates;
  return useMemo(
    () =>
      buildTranslatedContextMenuActions({
        hideReplace,
        hideRemoveText,
        hideCutText,
        hideSaveAsClip,
        hideAddPhoto,
        hideAddOverlayGraphic,
        hasOverlayHandler,
        addedPunchListItemId,
      }),
    [
      hideReplace,
      hideRemoveText,
      hideCutText,
      hideSaveAsClip,
      hideAddPhoto,
      hideAddOverlayGraphic,
      hasOverlayHandler,
      addedPunchListItemId,
    ],
  );
}
